import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { catalogApi } from '../api';
import { cartApi } from '../api';
import { useTelegram } from '../hooks/useTelegram';
import { useCart } from '../hooks/useCart';
import { Loading } from '../components/Loading';
import { Header } from '../components/Header';
import type { Product } from '../types';
import './ProductPage.css';

export const ProductPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { tg } = useTelegram();
  const { cart, fetchCart } = useCart();
  const [product, setProduct] = useState<Product | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [addingToCart, setAddingToCart] = useState(false);

  // Find cart item for this product
  const cartItem = cart?.items?.find(item => item.product_id === Number(id));
  const cartQuantity = cartItem?.quantity || 0;

  const loadProduct = useCallback(async (productId: number) => {
    try {
      setLoading(true);
      setError(null);
      const data = await catalogApi.getProduct(productId);
      setProduct(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Не удалось загрузить товар');
    } finally {
      setLoading(false);
    }
  }, []);

  const handleBack = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  useEffect(() => {
    if (id) {
      loadProduct(Number(id));
    }

    // Load cart to check if product is already in cart
    fetchCart();
  }, [id, loadProduct, fetchCart]);

  useEffect(() => {
    // Show back button
    const backHandler = () => {
      navigate(-1);
    };

    if (tg?.BackButton) {
      tg.BackButton.show();
      tg.BackButton.onClick(backHandler);
    }

    return () => {
      if (tg?.BackButton) {
        tg.BackButton.hide();
        tg.BackButton.offClick(backHandler);
      }
    };
  }, [tg, navigate]);

  const handleCartClick = () => {
    navigate('/cart');
  };

  const handleProfileClick = () => {
    navigate('/orders');
  };

  const handleAddToCart = async () => {
    if (!product || addingToCart) return;

    try {
      setAddingToCart(true);
      await cartApi.addItem(product.id, 1);
      await fetchCart();

      if (tg?.HapticFeedback) {
        tg.HapticFeedback.notificationOccurred('success');
      }
    } catch (err) {
      if (tg?.HapticFeedback) {
        tg.HapticFeedback.notificationOccurred('error');
      }
      setError(err instanceof Error ? err.message : 'Не удалось добавить товар');
    } finally {
      setAddingToCart(false);
    }
  };

  const handleUpdateQuantity = async (newQuantity: number) => {
    if (!cartItem || addingToCart) return;

    try {
      setAddingToCart(true);
      await cartApi.updateItem(cartItem.id, newQuantity);
      await fetchCart();

      if (tg?.HapticFeedback) {
        tg.HapticFeedback.selectionChanged();
      }
    } catch (err) {
      if (tg?.HapticFeedback) {
        tg.HapticFeedback.notificationOccurred('error');
      }
    } finally {
      setAddingToCart(false);
    }
  };

  const handleIncrease = () => {
    if (cartItem) {
      handleUpdateQuantity(cartQuantity + 1);
    } else {
      handleAddToCart();
    }
  };

  const handleDecrease = () => {
    if (cartItem && cartQuantity > 1) {
      handleUpdateQuantity(cartQuantity - 1);
    }
  };

  const formatPrice = (price: number) => {
    return `${price.toFixed(0)} ₽`;
  };

  const hasNutritionInfo = () => {
    if (!product) return false;
    return product.calories != null || product.protein != null || product.fat != null || product.carbohydrates != null;
  };

  if (loading) {
    return (
      <div className="page product-page">
        <Header cartItemsCount={cart?.items?.length || 0} onCartClick={handleCartClick} onProfileClick={handleProfileClick} />
        <Loading />
      </div>
    );
  }

  if (error || !product) {
    return (
      <div className="page product-page">
        <Header cartItemsCount={cart?.items?.length || 0} onCartClick={handleCartClick} onProfileClick={handleProfileClick} />
        <div className="error-container">
          <p className="error-message">{error || 'Товар не найден'}</p>
          <button className="retry-button" onClick={handleBack}>
            Вернуться назад
          </button>
        </div>
      </div>
    );
  }

  // Build images array: primary image first, then other images sorted by sort_order
  const buildImagesArray = (): string[] => {
    const images: string[] = [];

    // Добавляем главное изображение первым
    if (product.image_url) {
      images.push(product.image_url);
    }

    // Добавляем дополнительные изображения, отсортированные по sort_order
    if (product.images && product.images.length > 0) {
      const sortedImages = [...product.images]
        .sort((a, b) => a.sort_order - b.sort_order)
        .map(img => img.image_url);
      images.push(...sortedImages);
    }

    return images;
  };

  const images = buildImagesArray();
  const currentImage = images[selectedImageIndex] || 'https://via.placeholder.com/600x400?text=No+Image';

  const handlePrevImage = () => {
    setSelectedImageIndex((prev) => (prev === 0 ? images.length - 1 : prev - 1));
  };

  const handleNextImage = () => {
    setSelectedImageIndex((prev) => (prev === images.length - 1 ? 0 : prev + 1));
  };

  return (
    <div className="page product-page">
      <Header cartItemsCount={cart?.items?.length || 0} onCartClick={handleCartClick} onProfileClick={handleProfileClick} />
      <div className="product-gallery">
        <div className="gallery-main">
          <img
            src={currentImage}
            alt={product.name}
            className="gallery-main-image"
            onError={(e) => {
              e.currentTarget.src = 'https://via.placeholder.com/600x400?text=No+Image';
            }}
          />
          {!product.is_available && (
            <div className="product-unavailable-overlay">Нет в наличии</div>
          )}

          {images.length > 1 && (
            <>
              <button className="gallery-arrow gallery-arrow-left" onClick={handlePrevImage}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path d="M15 18L9 12L15 6" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
              <button className="gallery-arrow gallery-arrow-right" onClick={handleNextImage}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path d="M9 18L15 12L9 6" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
            </>
          )}
        </div>

        {images.length > 1 && (
          <div className="gallery-indicators">
            {images.map((_, index) => (
              <button
                key={index}
                className={`gallery-indicator ${selectedImageIndex === index ? 'active' : ''}`}
                onClick={() => setSelectedImageIndex(index)}
              />
            ))}
          </div>
        )}
      </div>

      <div className="product-details">
        <div className="product-header">
          <div className="product-title-section">
            <h1 className="product-title">{product.name}</h1>
            {product.category_name && (
              <span className="product-category">{product.category_name}</span>
            )}
          </div>
          <div className="product-price-large">{formatPrice(product.price)}</div>
        </div>

        {product.description && (
          <div className="product-description-section">
            <h2 className="section-title">Описание</h2>
            <p className="product-description-text">{product.description}</p>
          </div>
        )}

        <div className="product-info-section">
          <h2 className="section-title">Информация</h2>
          <div className="product-info-grid">
            {product.weight && (
              <div className="info-item">
                <span className="info-label">Вес:</span>
                <span className="info-value">{product.weight}г</span>
              </div>
            )}
            {product.pieces && (
              <div className="info-item">
                <span className="info-label">Количество:</span>
                <span className="info-value">{product.pieces} шт</span>
              </div>
            )}
          </div>

          {hasNutritionInfo() && (
            <>
              <h3 className="subsection-title">Пищевая ценность на 100 грамм</h3>
              <div className="product-info-grid">
                {product.calories != null && (
                  <div className="info-item">
                    <span className="info-label">Калории:</span>
                    <span className="info-value">{product.calories} ккал</span>
                  </div>
                )}
                {product.protein != null && (
                  <div className="info-item">
                    <span className="info-label">Белки:</span>
                    <span className="info-value">{product.protein}г</span>
                  </div>
                )}
                {product.fat != null && (
                  <div className="info-item">
                    <span className="info-label">Жиры:</span>
                    <span className="info-value">{product.fat}г</span>
                  </div>
                )}
                {product.carbohydrates != null && (
                  <div className="info-item">
                    <span className="info-label">Углеводы:</span>
                    <span className="info-value">{product.carbohydrates}г</span>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>

      <div className="product-page-footer">
        {cartQuantity > 0 ? (
          <div className="quantity-controls-large">
            <button
              className="quantity-btn-large"
              onClick={handleDecrease}
              disabled={addingToCart || cartQuantity <= 1}
            >
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <path d="M4 10H16" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"/>
              </svg>
            </button>

            <span className="quantity-value-large">{cartQuantity}</span>

            <button
              className="quantity-btn-large"
              onClick={handleIncrease}
              disabled={addingToCart || !product.is_available}
            >
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <path d="M10 4V16M4 10H16" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"/>
              </svg>
            </button>
          </div>
        ) : (
          <button
            className="add-to-cart-btn-large"
            onClick={handleAddToCart}
            disabled={addingToCart || !product.is_available}
          >
            {addingToCart ? 'Добавление...' : product.is_available ? 'Добавить в корзину' : 'Нет в наличии'}
          </button>
        )}
      </div>
    </div>
  );
};
